import { supabase, TABLES } from './supabase';
import { checkBlacklist } from './blacklist';

export interface TokenComment {
  id: string;
  token_address: string;
  commenter_address: string;
  message: string;
  tx_hash: string | null;
  block_number: number | null;
  likes: number;
  created_at: string;
  parent_comment_id: string | null;
  reply_count: number;
}

/**
 * Send a comment or reply (saved to Supabase only)
 * @param tokenAddress - The token contract address to comment on
 * @param message - The comment message (max 500 chars)
 * @param userAddress - The user's address
 * @param parentCommentId - Optional parent comment ID for replies
 * @returns Success status
 */
export async function sendTokenComment(
  tokenAddress: `0x${string}`,
  message: string,
  userAddress: `0x${string}`,
  parentCommentId?: string
): Promise<boolean> {
  if (message.length === 0 || message.length > 500) {
    throw new Error('Comment must be between 1 and 500 characters');
  }

  // Check blacklist before sending
  const blacklistCheck = checkBlacklist(message);
  if (blacklistCheck.isBlacklisted) {
    throw new Error('Comment contains inappropriate content. Please revise your comment.');
  }

  console.log('[TokenComments] Posting comment');
  console.log('[TokenComments] Token:', tokenAddress);
  console.log('[TokenComments] Message:', message);
  if (parentCommentId) {
    console.log('[TokenComments] Reply to:', parentCommentId);
  }

  // Save directly to Supabase
  const { error } = await supabase.from(TABLES.TOKEN_COMMENTS).insert({
    token_address: tokenAddress.toLowerCase(),
    commenter_address: userAddress.toLowerCase(),
    message,
    tx_hash: null,
    block_number: null,
    parent_comment_id: parentCommentId || null,
  });

  if (error) {
    console.error('[TokenComments] Error saving comment:', error);
    throw new Error('Failed to post comment');
  }

  console.log('[TokenComments] Comment posted successfully');
  return true;
}

/**
 * Fetch comments for a token from Supabase
 * @param tokenAddress - The token contract address
 * @param limit - Maximum number of comments to return
 * @returns Array of comments
 */
export async function fetchTokenComments(
  tokenAddress: string,
  limit: number = 20
): Promise<TokenComment[]> {
  try {
    const { data, error } = await supabase
      .from(TABLES.TOKEN_COMMENTS)
      .select('*')
      .ilike('token_address', tokenAddress)
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) {
      console.error('[TokenComments] Error fetching from Supabase:', error);
      return [];
    }

    return data || [];
  } catch (err) {
    console.error('[TokenComments] Exception fetching comments:', err);
    return [];
  }
}

/**
 * Like a comment (off-chain engagement metric)
 * TODO: Implement RPC function in Supabase for atomic increment
 */
export async function likeComment(commentId: string): Promise<boolean> {
  try {
    // For now, just increment the likes count
    const { data: comment } = await supabase
      .from(TABLES.TOKEN_COMMENTS)
      .select('likes')
      .eq('id', commentId)
      .single();

    if (comment) {
      const { error } = await supabase
        .from(TABLES.TOKEN_COMMENTS)
        .update({ likes: comment.likes + 1 })
        .eq('id', commentId);

      if (error) {
        console.error('[TokenComments] Error liking comment:', error);
        return false;
      }

      return true;
    }

    return false;
  } catch (err) {
    console.error('[TokenComments] Exception liking comment:', err);
    return false;
  }
}

